import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  MapPin,
  Search,
  CheckCircle2,
  XCircle,
  Landmark,
  ArrowRight,
  ScrollText,
  Users,
  Church,
  Download,
  Map as MapIcon,
  Shapes,
} from "lucide-react";
import { toast } from "sonner";
import api, { API } from "../lib/api";
import LeafletFOMMap from "../components/LeafletFOMMap";

/**
 * Public /field-of-membership page.
 *
 * Makes NCUA Field-of-Membership eligibility unmistakable:
 *   1. Big interactive ZIP checker (live /fom/check call, instant result)
 *   2. Stylized SVG map of the two counties (Fulton · DeKalb)
 *      with live supporter counts per community from /credit-union/stats
 *   3. WDLC narrative (why the three qualify as a Well-Defined Local Community)
 *   4. Searchable ZIP directory grouped by community
 *   5. CTA strip → /credit-union pledge, Church Pack download
 */
export default function FieldOfMembership() {
  const [zip, setZip] = useState("");
  const [checkResult, setCheckResult] = useState(null);
  const [checking, setChecking] = useState(false);
  const [stats, setStats] = useState(null);
  const [registry, setRegistry] = useState(null);
  const [zipFilter, setZipFilter] = useState("");

  useEffect(() => {
    api.get("/credit-union/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/credit-union/fom/zips").then((r) => setRegistry(r.data)).catch(() => {});
  }, []);

  const doCheck = async (incoming) => {
    const raw = (incoming ?? zip).replace(/\D/g, "").slice(0, 5);
    setZip(raw);
    if (raw.length !== 5) {
      setCheckResult(null);
      return;
    }
    setChecking(true);
    try {
      const r = await api.get(`/credit-union/fom/check?zip=${raw}`);
      setCheckResult(r.data);
      if (r.data?.in_fom) {
        toast.success(`ZIP ${raw} is inside the proposed Field of Membership — ${r.data.county}`);
      }
    } catch (_e) {
      setCheckResult({ zip: raw, in_fom: false, county: null });
    } finally {
      setChecking(false);
    }
  };

  const county = checkResult?.county;
  const filteredRegistry = useMemo(() => {
    if (!registry) return null;
    const q = zipFilter.trim();
    if (!q) return registry;
    const match = (list) => list.filter((z) => z.startsWith(q));
    return {
      ...registry,
      fulton: match(registry.fulton),
      dekalb: match(registry.dekalb),
    };
  }, [registry, zipFilter]);

  const byCounty = stats?.by_county || {};
  const fultonCount = byCounty.Fulton?.count || 0;
  const dekalbCount = byCounty.DeKalb?.count || 0;

  return (
    <div className="grain" data-testid="fom-page">
      {/* HERO */}
      <HeroSection stats={stats} />

      {/* ZIP CHECKER */}
      <section
        className="relative border-y border-bdu-gold/15"
        data-testid="fom-checker-section"
        style={{
          background:
            "radial-gradient(900px 400px at 50% 0%, rgba(212,175,55,0.12) 0%, rgba(0,0,0,0) 60%)",
        }}
      >
        <div className="max-w-4xl mx-auto px-6 sm:px-10 py-20">
          <div className="text-center">
            <div className="label-eyebrow">Instant Eligibility Check</div>
            <h2 className="mt-4 font-serif text-4xl sm:text-5xl text-bdu-cream tracking-tight">
              Enter your <span className="text-bdu-gold italic">ZIP</span>.
              <br className="sm:hidden" /> Know in a heartbeat.
            </h2>
            <p className="mt-5 text-bdu-cream/65 max-w-2xl mx-auto leading-relaxed">
              Our proposed Field of Membership covers Fulton County and DeKalb
              County, Georgia. Type any 5-digit ZIP to see where it lands.
            </p>
          </div>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              doCheck();
            }}
            className="mt-12 max-w-xl mx-auto"
          >
            <div
              className={`relative transition-all duration-500 ${
                checkResult?.in_fom
                  ? "shadow-[0_0_80px_-10px_rgba(212,175,55,0.6)]"
                  : checkResult && !checkResult.in_fom
                  ? "shadow-[0_0_80px_-10px_rgba(227,38,54,0.4)]"
                  : ""
              }`}
            >
              <Search
                size={20}
                className="absolute left-5 top-1/2 -translate-y-1/2 text-bdu-cream/40"
              />
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={5}
                value={zip}
                onChange={(e) => doCheck(e.target.value)}
                placeholder="e.g. 30309"
                data-testid="fom-zip-input"
                className="w-full bg-black border border-bdu-gold/30 text-bdu-cream text-3xl sm:text-4xl font-serif tracking-widest px-16 py-6 text-center outline-none focus:border-bdu-gold transition-colors"
              />
              <button
                type="submit"
                disabled={checking || zip.length !== 5}
                className="absolute right-3 top-1/2 -translate-y-1/2 px-5 py-2.5 text-xs uppercase tracking-widest font-semibold bg-bdu-gold text-black hover:brightness-110 disabled:opacity-40 disabled:cursor-not-allowed transition"
                data-testid="fom-zip-submit"
              >
                Check
              </button>
            </div>

            {/* Result card */}
            {checkResult && zip.length === 5 && (
              <ZipResultCard result={checkResult} />
            )}

            {/* Hints */}
            <div className="mt-6 text-center text-xs text-bdu-cream/40">
              Try <QuickZip onClick={doCheck} zip="30309" label="Atlanta" />
              {" · "}
              <QuickZip onClick={doCheck} zip="30030" label="Decatur" />
              {" · "}
              <QuickZip onClick={doCheck} zip="30342" label="Sandy Springs" />
            </div>
          </form>
        </div>
      </section>

      {/* MAP + LIVE SUPPORTER COUNTS */}
      <MapSection
        fultonCount={fultonCount}
        dekalbCount={dekalbCount}
        totalZips={registry?.total_zips}
        highlightCounty={county}
      />

      {/* WDLC NARRATIVE */}
      <WDLCSection />

      {/* ZIP DIRECTORY */}
      <section
        className="relative border-y border-bdu-gold/10"
        data-testid="fom-directory-section"
      >
        <div className="max-w-7xl mx-auto px-6 sm:px-10 py-24">
          <div className="flex items-end justify-between flex-wrap gap-6 mb-10">
            <div>
              <div className="label-eyebrow">ZIP Code Directory</div>
              <h2 className="mt-3 font-serif text-3xl sm:text-4xl text-bdu-cream">
                Every ZIP in the proposed FOM
              </h2>
              <p className="mt-2 text-sm text-bdu-cream/55 max-w-lg">
                Searchable list of all {registry?.total_zips || 100} ZIP codes
                inside the Well-Defined Local Community.
              </p>
            </div>
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-bdu-cream/40" />
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={5}
                value={zipFilter}
                onChange={(e) => setZipFilter(e.target.value.replace(/\D/g, ""))}
                placeholder="Filter…"
                data-testid="fom-directory-filter"
                className="bg-bdu-surface2 border border-bdu-gold/20 text-bdu-cream text-sm pl-9 pr-3 py-2 focus:border-bdu-gold outline-none transition w-40"
              />
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6" data-testid="fom-directory-grid">
            <ZipColumn
              title="Fulton County"
              zips={filteredRegistry?.fulton || []}
              color="#D4AF37"
              testId="fom-zips-fulton"
            />
            <ZipColumn
              title="DeKalb County"
              zips={filteredRegistry?.dekalb || []}
              color="#2EA44F"
              testId="fom-zips-dekalb"
            />
          </div>
        </div>
      </section>

      {/* CTA STRIP */}
      <CTASection />
    </div>
  );
}

// ---------- Hero ----------

function HeroSection({ stats }) {
  return (
    <section
      className="relative overflow-hidden border-b border-bdu-gold/10"
      data-testid="fom-hero"
    >
      <div
        className="absolute inset-0 opacity-25"
        style={{
          backgroundImage:
            "radial-gradient(1100px 500px at 20% 20%, rgba(212,175,55,0.25), transparent 60%)," +
            "radial-gradient(900px 500px at 85% 90%, rgba(46,164,79,0.18), transparent 60%)",
        }}
      />
      <div className="relative max-w-7xl mx-auto px-6 sm:px-10 py-24 lg:py-32 grid lg:grid-cols-12 gap-10 items-end">
        <div className="lg:col-span-8 animate-fade-up">
          <div className="label-eyebrow flex items-center gap-2">
            <ScrollText size={12} className="text-bdu-gold" /> NCUA Field of Membership · Public Record
          </div>
          <h1 className="mt-5 font-serif text-5xl sm:text-6xl lg:text-7xl leading-[0.98] text-bdu-cream tracking-tight">
            Who the fund is <span className="text-bdu-gold italic">for</span>.
            <br />
            Where the charter <span className="text-bdu-gold italic">serves</span>.
          </h1>
          <p className="mt-7 text-lg text-bdu-cream/75 max-w-2xl leading-relaxed">
            The proposed <span className="text-bdu-gold">Trillion Dollar Spending Power Federal Credit Union</span> will serve
            a Well-Defined Local Community of Fulton County and DeKalb County,
            Georgia — two contiguous counties bound by one Metropolitan
            Statistical Area, one transit system, and a shared Black
            commercial heritage.
          </p>
        </div>
        <div className="lg:col-span-4 flex lg:justify-end">
          <div className="grid grid-cols-2 gap-4 w-full max-w-sm">
            <StatBadge label="Counties" value="2" sub="Fulton · DeKalb" testId="fom-hero-counties" />
            <StatBadge
              label="MSA"
              value="12060"
              sub="Atlanta–Sandy Springs–Alpharetta"
              testId="fom-hero-msa"
              gold
            />
            <StatBadge
              label="ZIP Codes"
              value="97"
              sub="Inside the FOM"
              testId="fom-hero-zips"
            />
            <StatBadge
              label="Population"
              value="1.84M"
              sub="Potential members"
              testId="fom-hero-population"
              gold
            />
          </div>
        </div>
      </div>
    </section>
  );
}

function StatBadge({ label, value, sub, gold, testId }) {
  return (
    <div
      data-testid={testId}
      className={`relative border p-4 ${gold ? "border-bdu-gold/50 bg-bdu-gold/5" : "border-bdu-gold/20 bg-black/40"}`}
    >
      <div className="text-[9px] uppercase tracking-[0.26em] text-bdu-cream/50">
        {label}
      </div>
      <div className={`mt-2 font-serif text-3xl ${gold ? "text-bdu-gold" : "text-bdu-cream"}`}>
        {value}
      </div>
      <div className="mt-1 text-[11px] text-bdu-cream/55 leading-tight">{sub}</div>
    </div>
  );
}

// ---------- ZIP result ----------

function ZipResultCard({ result }) {
  if (!result) return null;
  const good = !!result.in_fom;
  const county = result.county || "Out-of-FOM";
  const colors = {
    Fulton: "#D4AF37",
    DeKalb: "#2EA44F",
  };
  const accent = colors[county] || "#A3A099";

  return (
    <div
      className={`mt-8 border p-6 sm:p-8 animate-fade-up ${
        good ? "border-bdu-gold/60 bg-bdu-gold/5" : "border-bdu-red/40 bg-bdu-red/5"
      }`}
      data-testid="fom-zip-result"
    >
      <div className="flex items-start gap-4">
        <div
          className={`flex-shrink-0 w-14 h-14 rounded-full flex items-center justify-center ${
            good ? "" : "bg-bdu-red/20"
          }`}
          style={good ? { backgroundColor: `${accent}30` } : undefined}
        >
          {good ? (
            <CheckCircle2 size={28} style={{ color: accent }} strokeWidth={2.2} />
          ) : (
            <XCircle size={28} className="text-bdu-red" strokeWidth={2.2} />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[11px] uppercase tracking-[0.28em] text-bdu-cream/55">
            ZIP {result.zip}
          </div>
          <div
            className="mt-1 font-serif text-2xl sm:text-3xl leading-tight"
            style={{ color: good ? accent : "#E32636" }}
            data-testid="fom-zip-result-headline"
          >
            {good
              ? `Eligible — ${county}`
              : "Outside the proposed FOM"}
          </div>
          <p className="mt-3 text-sm text-bdu-cream/70 leading-relaxed">
            {good
              ? "Your ZIP sits inside the Well-Defined Local Community for the proposed Trillion Dollar Spending Power Federal Credit Union. You are a Potential Member for charter-approval purposes."
              : "This ZIP is not currently inside the proposed FOM. You can still pledge support — out-of-FOM pledges are logged separately but do not count toward the NCUA Potential Member target. If the charter later expands, you may become eligible."}
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link
              to={good ? `/credit-union?zip=${result.zip}` : "/credit-union"}
              className="btn-gold text-xs"
              data-testid="fom-zip-result-cta"
            >
              Pledge Support <ArrowRight size={14} />
            </Link>
            {!good && (
              <Link to="/credit-union" className="btn-ghost text-xs">
                Pledge Anyway
              </Link>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function QuickZip({ onClick, zip, label }) {
  return (
    <button
      type="button"
      onClick={() => onClick(zip)}
      className="underline underline-offset-4 decoration-dotted text-bdu-cream/55 hover:text-bdu-gold transition"
      data-testid={`fom-quick-zip-${zip}`}
    >
      {zip} ({label})
    </button>
  );
}

// ---------- Map Section ----------

function MapSection({ fultonCount, dekalbCount, totalZips, highlightCounty }) {
  const totalIn = fultonCount + dekalbCount;
  const [mode, setMode] = useState("stylized"); // "stylized" | "gis"

  return (
    <section
      className="relative border-b border-bdu-gold/10 overflow-hidden"
      data-testid="fom-map-section"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-10 py-24 grid lg:grid-cols-12 gap-12 items-center">
        {/* Left: narrative */}
        <div className="lg:col-span-5">
          <div className="label-eyebrow flex items-center gap-2">
            <MapPin size={12} className="text-bdu-gold" /> The Three Communities
          </div>
          <h2 className="mt-5 font-serif text-4xl sm:text-5xl text-bdu-cream leading-[1.05] tracking-tight">
            One MSA. One <span className="text-bdu-gold italic">economy</span>.
            One <span className="text-bdu-gold italic">community</span>.
          </h2>
          <p className="mt-6 text-bdu-cream/70 leading-relaxed">
            Fulton anchors downtown Atlanta and the historic Black commercial
            corridor along Auburn Avenue, plus the airport-corridor cities of
            East Point, College Park and Hapeville. DeKalb carries Decatur,
            Clarkston, Doraville, Brookhaven, Tucker and the eastern suburbs.
          </p>
          <p className="mt-4 text-bdu-cream/60 text-sm italic leading-relaxed">
            Together, {totalZips || 97} ZIP codes · roughly 1.84 million residents · one Well-Defined Local Community.
          </p>

          <div className="mt-8 space-y-3" data-testid="fom-map-legend">
            <LegendRow
              color="#D4AF37"
              label="Fulton County"
              detail="Atlanta · Sandy Springs · Alpharetta · Roswell · East Point · College Park"
              count={fultonCount}
              highlighted={highlightCounty === "Fulton"}
              testId="fom-legend-fulton"
            />
            <LegendRow
              color="#2EA44F"
              label="DeKalb County"
              detail="Decatur · Clarkston · Doraville · Brookhaven · Tucker"
              count={dekalbCount}
              highlighted={highlightCounty === "DeKalb"}
              testId="fom-legend-dekalb"
            />
          </div>

          <div className="mt-8 flex items-center gap-3 text-xs text-bdu-cream/50">
            <Users size={14} className="text-bdu-gold" />
            <span>
              <span className="text-bdu-cream font-semibold">{totalIn}</span> in-FOM charter supporters so far
            </span>
          </div>
        </div>

        {/* Right: Map + toggle */}
        <div className="lg:col-span-7">
          {/* Toggle + Snapshot download */}
          <div
            className="mb-5 flex flex-wrap items-center justify-between gap-3"
            data-testid="fom-map-controls"
          >
            <div
              className="inline-flex items-center border border-bdu-gold/25 p-1 bg-black/50 backdrop-blur-sm"
              data-testid="fom-map-mode-toggle"
              role="tablist"
              aria-label="Map view mode"
            >
              <ModeTab
                active={mode === "stylized"}
                onClick={() => setMode("stylized")}
                icon={Shapes}
                label="Stylized"
                sub="Brand diagram"
                testId="fom-map-mode-stylized"
              />
              <ModeTab
                active={mode === "gis"}
                onClick={() => setMode("gis")}
                icon={MapIcon}
                label="GIS Overlay"
                sub="US Census TIGER · OSM"
                testId="fom-map-mode-gis"
              />
            </div>
            <a
              href={`${API}/credit-union/fom/snapshot.png`}
              download
              className="btn-ghost text-xs flex items-center gap-2"
              data-testid="fom-map-snapshot-btn"
              title="Download the composed OSM + boundary PNG for the NCUA FOM Study appendix"
            >
              <Download size={14} /> Download Map Snapshot (PNG)
            </a>
          </div>

          <div
            className="relative border border-bdu-gold/20 bg-black/50 p-6 sm:p-10"
            data-testid="fom-map-svg-wrapper"
            style={{
              boxShadow:
                "0 50px 100px -40px rgba(212,175,55,0.3), inset 0 1px 0 rgba(212,175,55,0.08)",
            }}
          >
            {mode === "stylized" ? (
              <AtlantaMetroMap highlightCounty={highlightCounty} />
            ) : (
              <LeafletFOMMap active={mode === "gis"} highlightCommunity={highlightCounty} />
            )}
          </div>
          <p className="mt-4 text-center text-[11px] text-bdu-cream/40 italic">
            {mode === "stylized"
              ? "Stylized boundary diagram · not to GIS scale · see directory below for the authoritative ZIP list."
              : "Real-world boundaries · US Census TIGER (simplified) over OpenStreetMap · click a region for details."}
          </p>
        </div>
      </div>
    </section>
  );
}

function ModeTab({ active, onClick, icon: Icon, label, sub, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-selected={active}
      role="tab"
      data-testid={testId}
      className={`relative flex items-center gap-2.5 px-4 py-2 text-xs uppercase tracking-[0.2em] transition-all ${
        active
          ? "text-black bg-bdu-gold shadow-[0_6px_20px_-6px_rgba(212,175,55,0.6)]"
          : "text-bdu-cream/55 hover:text-bdu-gold"
      }`}
    >
      <Icon size={13} />
      <span className="flex flex-col items-start leading-tight">
        <span className="font-semibold">{label}</span>
        <span className={`text-[9px] tracking-[0.18em] ${active ? "text-black/60" : "text-bdu-cream/40"}`}>
          {sub}
        </span>
      </span>
    </button>
  );
}

function LegendRow({ color, label, detail, count, highlighted, testId }) {
  return (
    <div
      data-testid={testId}
      className={`flex items-start gap-3 p-3 border transition-all ${
        highlighted ? "border-bdu-gold bg-bdu-gold/10" : "border-bdu-gold/15"
      }`}
    >
      <div
        className="flex-shrink-0 w-3 h-3 mt-1.5 rounded-full"
        style={{ backgroundColor: color, boxShadow: `0 0 12px ${color}` }}
      />
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline justify-between gap-4">
          <div className="font-serif text-base text-bdu-cream">{label}</div>
          <div className="text-xs text-bdu-cream/55 whitespace-nowrap">
            <span className="font-semibold text-bdu-cream">{count || 0}</span> supporters
          </div>
        </div>
        <div className="text-xs text-bdu-cream/50 leading-snug mt-0.5">{detail}</div>
      </div>
    </div>
  );
}

/**
 * Stylized SVG map of the three FOM communities.
 * Shapes are geometric approximations — NOT GIS-accurate — meant to clearly
 * communicate the two counties and their relative positions.
 * The ZIP directory below is the authoritative registry.
 */
function AtlantaMetroMap({ highlightCounty }) {
  const dim = (name) => (highlightCounty && highlightCounty !== name ? 0.4 : 1);

  return (
    <svg
      viewBox="0 0 500 420"
      className="w-full h-auto"
      role="img"
      aria-label="Proposed Field of Membership — Fulton, DeKalb"
      data-testid="fom-map-svg"
    >
      <defs>
        <radialGradient id="goldGlow" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="rgba(212,175,55,0.6)" />
          <stop offset="100%" stopColor="rgba(212,175,55,0)" />
        </radialGradient>
        <linearGradient id="fultonFill" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#D4AF37" stopOpacity="0.85" />
          <stop offset="100%" stopColor="#8B6914" stopOpacity="0.95" />
        </linearGradient>
        <linearGradient id="dekalbFill" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#2EA44F" stopOpacity="0.85" />
          <stop offset="100%" stopColor="#166534" stopOpacity="0.95" />
        </linearGradient>
        <linearGradient id="cpFill" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#E32636" stopOpacity="0.9" />
          <stop offset="100%" stopColor="#8B0000" stopOpacity="0.95" />
        </linearGradient>
      </defs>

      {/* Backdrop grid */}
      <g opacity="0.18">
        {Array.from({ length: 10 }).map((_, i) => (
          <line
            key={`v${i}`}
            x1={i * 50}
            y1="0"
            x2={i * 50}
            y2="420"
            stroke="#D4AF37"
            strokeWidth="0.3"
          />
        ))}
        {Array.from({ length: 9 }).map((_, i) => (
          <line
            key={`h${i}`}
            x1="0"
            y1={i * 50}
            x2="500"
            y2={i * 50}
            stroke="#D4AF37"
            strokeWidth="0.3"
          />
        ))}
      </g>

      {/* Fulton — vertical spine on the left, tapered south */}
      <g opacity={dim("Fulton")} style={{ transition: "opacity 400ms" }}>
        <path
          d="M 90 30 L 220 25 L 240 90 L 230 180 L 215 260 L 180 340 L 150 365 L 115 345 L 95 280 L 85 190 L 80 100 Z"
          fill="url(#fultonFill)"
          stroke="#D4AF37"
          strokeWidth="1.5"
          data-testid="fom-map-fulton"
        />
        <text
          x="155"
          y="100"
          textAnchor="middle"
          fontFamily="Cormorant Garamond, serif"
          fontSize="22"
          fontWeight="700"
          fill="#FDFBF7"
          letterSpacing="1"
        >
          FULTON
        </text>
        <text
          x="155"
          y="122"
          textAnchor="middle"
          fontFamily="Helvetica"
          fontSize="9"
          letterSpacing="2"
          fill="rgba(253,251,247,0.75)"
        >
          ATLANTA · SANDY SPRINGS
        </text>
        <text
          x="155"
          y="136"
          textAnchor="middle"
          fontFamily="Helvetica"
          fontSize="9"
          letterSpacing="2"
          fill="rgba(253,251,247,0.75)"
        >
          ROSWELL · ALPHARETTA
        </text>
        {/* Fulton star marker */}
        <circle cx="155" cy="210" r="4" fill="#FDFBF7" />
        <text
          x="165"
          y="214"
          fontFamily="Helvetica"
          fontSize="8"
          letterSpacing="1"
          fill="rgba(253,251,247,0.85)"
        >
          Downtown Atlanta
        </text>
      </g>

      {/* DeKalb — east of Fulton */}
      <g opacity={dim("DeKalb")} style={{ transition: "opacity 400ms" }}>
        <path
          d="M 240 90 L 400 85 L 440 120 L 450 200 L 430 280 L 395 320 L 340 310 L 290 290 L 240 260 L 230 180 Z"
          fill="url(#dekalbFill)"
          stroke="#2EA44F"
          strokeWidth="1.5"
          data-testid="fom-map-dekalb"
        />
        <text
          x="340"
          y="160"
          textAnchor="middle"
          fontFamily="Cormorant Garamond, serif"
          fontSize="22"
          fontWeight="700"
          fill="#FDFBF7"
          letterSpacing="1"
        >
          DEKALB
        </text>
        <text
          x="340"
          y="182"
          textAnchor="middle"
          fontFamily="Helvetica"
          fontSize="9"
          letterSpacing="2"
          fill="rgba(253,251,247,0.75)"
        >
          DECATUR · BROOKHAVEN
        </text>
        <text
          x="340"
          y="196"
          textAnchor="middle"
          fontFamily="Helvetica"
          fontSize="9"
          letterSpacing="2"
          fill="rgba(253,251,247,0.75)"
        >
          DUNWOODY · CLARKSTON
        </text>
        <circle cx="310" cy="240" r="4" fill="#FDFBF7" />
        <text
          x="320"
          y="244"
          fontFamily="Helvetica"
          fontSize="8"
          letterSpacing="1"
          fill="rgba(253,251,247,0.85)"
        >
          Decatur Square
        </text>
      </g>

      {/* College Park shape removed in 2026-02 FOM scope revision — ZIPs 30337 / 30349 / 30354 now sit inside Fulton. */}

      {/* Compass */}
      <g transform="translate(445,30)">
        <circle cx="0" cy="0" r="16" fill="none" stroke="#D4AF37" strokeOpacity="0.4" strokeWidth="0.8" />
        <text
          x="0"
          y="-18"
          textAnchor="middle"
          fontFamily="Helvetica"
          fontSize="8"
          fill="#D4AF37"
          letterSpacing="1"
        >
          N
        </text>
        <path d="M 0 -10 L 3 2 L 0 0 L -3 2 Z" fill="#D4AF37" />
      </g>

      {/* State label */}
      <text
        x="500"
        y="415"
        textAnchor="end"
        fontFamily="Helvetica"
        fontSize="8"
        letterSpacing="3"
        fill="rgba(212,175,55,0.5)"
      >
        STATE OF GEORGIA
      </text>
    </svg>
  );
}

// ---------- WDLC Narrative ----------

function WDLCSection() {
  return (
    <section
      className="relative"
      data-testid="fom-wdlc-section"
      style={{
        background:
          "radial-gradient(900px 400px at 100% 50%, rgba(46,164,79,0.1) 0%, rgba(0,0,0,0) 60%)",
      }}
    >
      <div className="max-w-5xl mx-auto px-6 sm:px-10 py-24">
        <div className="label-eyebrow">Why These Two · The WDLC Case</div>
        <h2 className="mt-5 font-serif text-3xl sm:text-4xl lg:text-5xl text-bdu-cream leading-tight max-w-4xl">
          A Well-Defined Local Community under
          <span className="text-bdu-gold italic"> NCUA Chapter 2, Appendix B</span>.
        </h2>

        <div className="mt-10 grid md:grid-cols-2 gap-10 text-bdu-cream/75 text-[15px] leading-relaxed">
          <div>
            <h3 className="font-serif text-xl text-bdu-cream">One MSA. One economy.</h3>
            <p className="mt-3">
              Fulton and DeKalb both sit inside the Atlanta–Sandy
              Springs–Alpharetta Metropolitan Statistical Area — the Census
              Bureau's definition of an integrated economic region. Workers
              cross the county line daily; employers draw from both counties.
            </p>
          </div>
          <div>
            <h3 className="font-serif text-xl text-bdu-cream">One transit backbone.</h3>
            <p className="mt-3">
              MARTA heavy rail and bus service weaves through Fulton and DeKalb
              on a single interoperable network — the two counties are
              physically and functionally one commute.
            </p>
          </div>
          <div>
            <h3 className="font-serif text-xl text-bdu-cream">Shared institutions.</h3>
            <p className="mt-3">
              Common court jurisdictions (Fulton &amp; DeKalb Superior Courts),
              overlapping school districts at municipal lines, and the shared
              public health district under the Georgia DPH.
            </p>
          </div>
          <div>
            <h3 className="font-serif text-xl text-bdu-cream">The historic Black corridor.</h3>
            <p className="mt-3">
              Auburn Avenue, Cascade Road, Buford Highway, and the East Atlanta /
              Decatur corridor form a single Black commercial corridor with a
              shared 100-year commercial history, overlapping customer bases,
              and deeply interconnected family and faith networks.
            </p>
          </div>
        </div>

        <div
          className="mt-14 border-l-2 border-bdu-gold pl-6 py-2"
          data-testid="fom-wdlc-quote"
        >
          <p className="font-serif text-xl sm:text-2xl text-bdu-cream/85 italic leading-relaxed max-w-3xl">
            "A community charter may serve persons who live, work, worship,
            attend school in, or participate in associations headquartered in,
            or participate in programs to alleviate or eliminate poverty or
            distress which are located in, a Well-Defined Local Community."
          </p>
          <footer className="mt-3 text-xs uppercase tracking-[0.26em] text-bdu-gold">
            NCUA Chartering Manual · Chapter 2 · Appendix B
          </footer>
        </div>
      </div>
    </section>
  );
}

// ---------- ZIP column ----------

function ZipColumn({ title, zips, color, testId, note }) {
  return (
    <div className="border border-bdu-gold/15 bg-black/40 p-6" data-testid={testId}>
      <div className="flex items-center gap-3 mb-5">
        <div
          className="w-2.5 h-2.5 rounded-full"
          style={{ backgroundColor: color, boxShadow: `0 0 10px ${color}` }}
        />
        <div>
          <div className="font-serif text-xl text-bdu-cream">{title}</div>
          <div className="text-xs text-bdu-cream/50">
            {zips.length} ZIP{zips.length === 1 ? "" : "s"}
            {note ? ` · ${note}` : ""}
          </div>
        </div>
      </div>
      {zips.length === 0 ? (
        <div className="text-sm text-bdu-cream/40 italic py-4 text-center">
          No ZIPs match filter.
        </div>
      ) : (
        <div className="flex flex-wrap gap-1.5 max-h-80 overflow-y-auto pr-1">
          {zips.map((z) => (
            <code
              key={z}
              className="text-[11px] px-2 py-1 bg-bdu-surface2 text-bdu-cream/70 font-mono tracking-wider border border-bdu-gold/5 hover:border-bdu-gold/30 hover:text-bdu-gold transition-colors"
            >
              {z}
            </code>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------- CTA ----------

function CTASection() {
  return (
    <section
      className="relative py-24 border-t border-bdu-gold/10"
      data-testid="fom-cta-section"
    >
      <div
        className="absolute inset-0 opacity-50 pointer-events-none"
        style={{
          background:
            "radial-gradient(900px 400px at 50% 50%, rgba(212,175,55,0.15), transparent 60%)",
        }}
      />
      <div className="relative max-w-5xl mx-auto px-6 sm:px-10 text-center">
        <div className="label-eyebrow">Eligibility Confirmed · The Fund Awaits</div>
        <h2 className="mt-5 font-serif text-4xl sm:text-5xl lg:text-6xl text-bdu-cream leading-tight">
          If your ZIP is on this map, <span className="text-bdu-gold italic">the charter counts you.</span>
        </h2>
        <p className="mt-6 text-bdu-cream/65 max-w-2xl mx-auto leading-relaxed">
          Every in-FOM pledge of support strengthens the Potential Member case
          we submit to the NCUA. Add your name — your pledge is held in escrow
          at Citizen Trust Bank and refundable in full if the charter is not
          granted.
        </p>
        <div className="mt-10 flex flex-wrap justify-center gap-3">
          <Link to="/credit-union" className="btn-gold px-7 py-3.5" data-testid="fom-cta-pledge">
            <Landmark size={16} /> Pledge Your Support
          </Link>
          <a
            href={`${API}/credit-union/outreach/church-pack.pdf`}
            download
            className="btn-ghost px-7 py-3.5"
            data-testid="fom-cta-church-pack"
          >
            <Download size={16} /> Church Outreach Kit
          </a>
          <Link
            to="/credit-union#leaderboard"
            className="btn-ghost px-7 py-3.5"
            data-testid="fom-cta-rally"
          >
            <Church size={16} /> Rally Your Circle
          </Link>
        </div>
        <p className="mt-8 text-[11px] text-bdu-cream/40 italic max-w-xl mx-auto leading-relaxed">
          Proposed Credit Union · pending NCUA charter approval · pledges are
          non-binding and not deposits. Escrow at Citizen Trust Bank.
        </p>
      </div>
    </section>
  );
}
